import { useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { Button } from '@/components/UI';
import type { StrategyEditorChange } from '../strategyEditorChanges';
import { STRATEGY_EDITOR_SECTIONS, type StrategyEditorSectionId } from '../strategyEditorSections';

interface StrategySaveBarProps {
    mode: 'create' | 'edit';
    changes: StrategyEditorChange[];
    canEdit: boolean;
    isSaving: boolean;
    /** Why the last save attempt failed. Shown here so a failure never costs the user their edits. */
    errorMessage: string | null;
    onDiscard: () => void;
    onSave: () => void;
}

const sectionLabelById = new Map<StrategyEditorSectionId, string>(STRATEGY_EDITOR_SECTIONS.map((section) => [section.id, section.label]));

/**
 * The editor's single commit point. Every surface on the page stages its edits into form state and
 * lands here, so there is exactly one answer to "what does the save button cover?". In edit mode it
 * only appears once something actually changed.
 */
const StrategySaveBar = ({ mode, changes, canEdit, isSaving, errorMessage, onDiscard, onSave }: StrategySaveBarProps) => {
    const [isChangeListOpen, setIsChangeListOpen] = useState(false);

    const isCreateMode = mode === 'create';

    if (!isCreateMode && changes.length === 0) return null;

    const changeCountLabel = `${changes.length} unsaved ${changes.length === 1 ? 'change' : 'changes'}`;

    return (
        <div
            className={`sticky bottom-0 z-30 mt-6 -mx-4 border-t bg-[#161616]/95 px-4 py-3 backdrop-blur ${
                errorMessage === null ? 'border-yellow-600/60' : 'border-red-600/70'
            }`}
        >
            {errorMessage !== null && (
                <div className="mb-3 rounded-md border border-red-900/60 bg-red-950/40 p-3 text-sm text-red-100" role="alert">
                    {errorMessage}
                </div>
            )}
            {isChangeListOpen && changes.length > 0 && (
                <ul className="mb-3 max-h-48 space-y-1 overflow-y-auto rounded-md border border-zinc-800 bg-zinc-950/60 p-3">
                    {changes.map((change) => (
                        <li className="flex items-baseline gap-2 text-xs" key={`${change.section}:${change.label}`}>
                            <span className="w-16 shrink-0 font-mono text-[10px] uppercase tracking-wide text-zinc-600">
                                {sectionLabelById.get(change.section) ?? change.section}
                            </span>
                            <span className="text-zinc-300">{change.label}</span>
                        </li>
                    ))}
                </ul>
            )}

            <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1 text-sm text-zinc-400">
                    {isCreateMode ? (
                        'This strategy has not been created yet.'
                    ) : (
                        <button
                            aria-expanded={isChangeListOpen}
                            className="inline-flex items-center gap-2 rounded text-left hover:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-p314-blue"
                            type="button"
                            onClick={() => setIsChangeListOpen((previousIsOpen) => !previousIsOpen)}
                        >
                            <span className="font-medium text-yellow-400">{changeCountLabel}</span>
                            {!isChangeListOpen && changes[0] && <span className="truncate text-zinc-500">· {changes[0].label}</span>}
                            <span aria-hidden="true" className="text-[10px] text-zinc-500">
                                {isChangeListOpen ? <FaChevronUp /> : <FaChevronDown />}
                            </span>
                        </button>
                    )}
                </div>

                {!isCreateMode && (
                    <Button disabled={isSaving} size="sm" variant="outline-dark" onClick={onDiscard}>
                        Discard
                    </Button>
                )}
                <Button
                    data-trade-logic-v2-target="submit-strategy"
                    disabled={!canEdit || isSaving}
                    size="sm"
                    variant="primary"
                    onClick={onSave}
                >
                    {isSaving ? 'Saving…' : isCreateMode ? 'Create strategy' : 'Save changes'}
                </Button>
            </div>
        </div>
    );
};

export default StrategySaveBar;
